import {
  startTransition,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import * as Sentry from '@sentry/react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getErrorMessage } from '@/hooks/api/errorHelpers';
import {
  useConfigureOrgSSO,
  useInitializeOrgSSO,
  useOrgSSOStatus,
} from '@/hooks/api/useSSOSettings';
import {
  useScopeChangeAction,
  useUnsavedChangeActions,
} from '@/hooks/useScopeChangeHooks';
import { configureSSOBodySchema } from '@kirin/api-client/contracts/sso-settings';

import { CopyField } from './CopyField';
import { RequiredIdpSettings } from './RequiredIdpSettings';
import { SsoStatusError } from './SsoStatusError';

export interface ConnectSamlSsoModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  organizationId: string;
  onRequestDisconnect: () => void;
  /** When true, all write actions (configure, update, generate) are disabled. */
  readOnly?: boolean;
}

function renderInitializeButton(args: {
  isPending: boolean;
  onClick: () => void;
  readOnly?: boolean;
}): ReactElement {
  return (
    <Button
      type="button"
      size="sm"
      onClick={args.onClick}
      disabled={args.isPending || (args.readOnly ?? false)}
    >
      {args.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
      Generate SP credentials
    </Button>
  );
}

function renderConfigureButton(args: {
  isPending: boolean;
  disabled: boolean;
  readOnly?: boolean;
}): ReactElement {
  return (
    <Button
      type="submit"
      form="saml-config-form"
      size="sm"
      disabled={args.isPending || args.disabled || (args.readOnly ?? false)}
    >
      {args.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
      Configure SSO
    </Button>
  );
}

function renderUpdateButton(args: {
  isPending: boolean;
  disabled: boolean;
  readOnly?: boolean;
}): ReactElement {
  return (
    <Button
      type="submit"
      form="saml-config-form"
      size="sm"
      disabled={args.isPending || args.disabled || (args.readOnly ?? false)}
    >
      {args.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
      Update SSO
    </Button>
  );
}

export function ConnectSamlSsoModal(
  props: ConnectSamlSsoModalProps
): ReactElement {
  const {
    open,
    onOpenChange,
    organizationId,
    onRequestDisconnect,
    readOnly = false,
  } = props;

  const {
    data: sso,
    isLoading,
    isError: isStatusError,
    error: statusError,
    refetch: refetchStatus,
    isFetching: isFetchingStatus,
  } = useOrgSSOStatus(organizationId);

  const configureMutation = useConfigureOrgSSO(organizationId);
  const initializeMutation = useInitializeOrgSSO(organizationId);

  const { markDirty, markClean } = useUnsavedChangeActions();
  const { changeScopeTo } = useScopeChangeAction();
  const scopeId = `saml-sso-integration-${organizationId}`;

  const resetConfigureMutationRef = useRef<() => void>(() => {});

  useEffect(() => {
    resetConfigureMutationRef.current = (): void => {
      configureMutation.reset();
    };
  }, [configureMutation]);

  const [metadataUrl, setMetadataUrl] = useState('');
  const [domain, setDomain] = useState('');
  const [formError, setFormError] = useState('');
  const [domainWarning, setDomainWarning] = useState(false);
  const [formOpen, setFormOpen] = useState<boolean | null>(null);
  const [oktaOpen, setOktaOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  const isFormOpen = formOpen ?? (sso ? !sso.isConfigured : false);
  const noLinkedEmailDomain =
    !!sso && sso.isConfigured && sso.domains.length === 0;

  useEffect(() => {
    if (!noLinkedEmailDomain) return;
    resetConfigureMutationRef.current();
    startTransition(() => {
      setFormOpen(false);
      setMetadataUrl('');
      setDomain('');
      setFormError('');
      setDomainWarning(false);
    });
  }, [noLinkedEmailDomain]);

  const isDirty = metadataUrl.trim().length > 0 || domain.trim().length > 0;

  useEffect(() => {
    if (isDirty) {
      markDirty(scopeId);
    } else {
      markClean(scopeId);
    }
    return () => {
      markClean(scopeId);
    };
  }, [isDirty, markDirty, markClean, scopeId]);

  const resetFormState = (): void => {
    setMetadataUrl('');
    setDomain('');
    setFormError('');
    setDomainWarning(false);
    configureMutation.reset();
    markClean(scopeId);
  };

  const handleOpenChange = (nextOpen: boolean): void => {
    if (nextOpen) {
      return;
    }
    if (configureMutation.isPending) return;
    if (!isDirty) {
      onOpenChange(false);
      return;
    }
    changeScopeTo({
      from: scopeId,
      id: 'close-saml-sso-integration-modal',
      title: 'Unsaved changes',
      body: 'You have unsaved changes that will be lost.',
      cancelLabel: 'Keep Editing',
      discardLabel: 'Discard',
      run: () => {
        resetFormState();
        onOpenChange(false);
      },
    });
  };

  const handleConfigure = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    configureMutation.reset();
    setFormError('');
    setDomainWarning(false);
    const parsed = configureSSOBodySchema.safeParse({
      metadataUrl: metadataUrl.trim(),
      domain: domain.trim(),
    });
    if (!parsed.success) {
      const fe = parsed.error.flatten().fieldErrors;
      setFormError(
        fe.metadataUrl?.[0] ??
          fe.domain?.[0] ??
          'Check the email domain and metadata URL.'
      );
      return;
    }
    const { metadataUrl: validMetadataUrl, domain: validDomain } = parsed.data;
    const submittedDomain = validDomain;
    try {
      const result = await configureMutation.mutateAsync({
        metadataUrl: validMetadataUrl,
        domain: validDomain,
      });
      setMetadataUrl('');
      setDomain('');
      markClean(scopeId);
      if (
        !result.domains
          .map(d => d.toLowerCase())
          .includes(submittedDomain.toLowerCase())
      ) {
        setDomainWarning(true);
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: { error_type: 'saml_configure' },
        extra: { organizationId },
      });
      setFormError(getErrorMessage(err));
    }
  };

  const handleToggleForm = (): void => {
    const willOpen = !(formOpen ?? false);
    setFormOpen(willOpen);
    if (!willOpen) {
      configureMutation.reset();
      setFormError('');
      setDomainWarning(false);
    }
  };

  const submitLabel = sso?.isConfigured ? 'Update SSO' : 'Configure SSO';

  const renderFooterPrimary = (): ReactNode => {
    if (!sso) return null;

    if (!sso.isConfigured && !sso.ssoId) {
      const initializeError = initializeMutation.isError
        ? getErrorMessage(initializeMutation.error)
        : undefined;
      return (
        <div
          data-testid="saml-init-footer"
          className="flex flex-col items-end gap-1"
        >
          {renderInitializeButton({
            isPending: initializeMutation.isPending,
            onClick: () =>
              initializeMutation.mutate(undefined, {
                onError: err =>
                  Sentry.captureException(err, {
                    tags: { error_type: 'sso_initialize' },
                    extra: { organizationId },
                  }),
              }),
            readOnly,
          })}
          {initializeError && (
            <p
              data-testid="saml-init-footer-error"
              className="text-xs text-destructive"
            >
              {initializeError}
            </p>
          )}
        </div>
      );
    }

    if (!sso.isConfigured && sso.ssoId) {
      if (!isFormOpen) return null;
      return renderConfigureButton({
        isPending: configureMutation.isPending,
        disabled: !metadataUrl || !domain,
        readOnly,
      });
    }

    if (sso.isConfigured && !noLinkedEmailDomain) {
      if (!isFormOpen) return null;
      return renderUpdateButton({
        isPending: configureMutation.isPending,
        disabled: !metadataUrl || !domain,
        readOnly,
      });
    }

    return null;
  };

  const renderConfigForm = (className: string): ReactElement => (
    <form
      id="saml-config-form"
      noValidate
      onSubmit={handleConfigure}
      className={className}
    >
      <div className="space-y-1">
        <Label htmlFor="sso-domain" className="text-xs">
          Email domain
        </Label>
        <Input
          id="sso-domain"
          type="text"
          placeholder="acme.com"
          value={domain}
          onChange={e => setDomain(e.target.value)}
          required
          className="h-8 text-sm font-mono placeholder:text-muted-foreground/40 placeholder:not-italic"
        />
        <p className="text-xs text-muted-foreground">
          Employees with this email domain are auto-routed to your IdP at
          sign-in.
        </p>
      </div>
      <div className="space-y-1">
        <Label htmlFor="sso-metadata-url" className="text-xs">
          IdP Metadata URL
        </Label>
        <Input
          id="sso-metadata-url"
          type="text"
          inputMode="url"
          autoComplete="url"
          placeholder="https://your-idp.example.com/.../metadata"
          value={metadataUrl}
          onChange={e => setMetadataUrl(e.target.value)}
          required
          className="h-8 text-sm font-mono placeholder:text-muted-foreground/40 placeholder:not-italic"
        />
        <p className="text-xs text-muted-foreground">
          SAML metadata URL from your IdP. In Okta: App → Sign On → SAML 2.0 →
          &quot;Identity Provider metadata&quot;.
        </p>
      </div>
      {formError && <p className="text-xs text-destructive">{formError}</p>}
      {configureMutation.isSuccess && !domainWarning && (
        <p className="flex items-center gap-1 text-xs text-green-600">
          <CheckCircle2 className="h-3 w-3" />
          {submitLabel === 'Update SSO'
            ? 'SSO updated successfully.'
            : 'SSO configured successfully.'}
        </p>
      )}
      {configureMutation.isSuccess && domainWarning && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            SSO was saved but the domain <strong>was not applied</strong> — it
            is already assigned to another SSO connection. Contact support for
            assistance.
          </span>
        </div>
      )}
    </form>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col p-0 gap-0 text-foreground">
        <DialogHeader className="shrink-0 px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <DialogTitle className="text-primary">SAML SSO</DialogTitle>
            {!isLoading && sso && (
              <Badge variant={sso.isConfigured ? 'default' : 'secondary'}>
                {sso.isConfigured ? 'Configured' : 'Not configured'}
              </Badge>
            )}
          </div>
          <DialogDescription>
            Configure SAML 2.0 single sign-on with your identity provider.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading SSO settings…
            </div>
          )}

          {isStatusError && !sso && (
            <SsoStatusError
              error={statusError}
              onRetry={() => {
                void refetchStatus();
              }}
              isRetrying={isFetchingStatus}
            />
          )}

          {sso && (
            <>
              {sso.isConfigured ? (
                <div className="rounded-md border bg-muted/30 p-4 space-y-3">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Active configuration
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="w-28 shrink-0 pt-0.5 text-xs text-muted-foreground">
                      Domain(s)
                    </span>
                    {sso.domains.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {sso.domains.map(d => (
                          <Badge
                            key={d}
                            variant="outline"
                            className="font-mono text-xs"
                          >
                            {d}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-1 flex-col gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <p>
                            No email domain is linked for SSO sign-in routing.
                            This usually means the domain you entered is already
                            assigned to another SSO connection. Use{' '}
                            <strong>Disconnect</strong> to start over with a
                            different domain, or contact support if you need
                            that domain released.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="w-28 shrink-0 pt-0.5 text-xs text-muted-foreground">
                      IdP Metadata
                    </span>
                    <div className="flex-1 min-w-0">
                      <CopyField
                        label=""
                        value={sso.idpMetadataUrl ?? ''}
                        sensitive
                      />
                    </div>
                  </div>

                  <div className="border-t pt-3">
                    <button
                      type="button"
                      onClick={() => setConfigOpen(o => !o)}
                      className="flex w-full items-center justify-between text-left mb-3"
                    >
                      <div>
                        <h4 className="text-sm font-medium">
                          Service provider settings
                        </h4>
                        {!configOpen && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            View Entity ID, ACS URL, and required IdP settings
                          </p>
                        )}
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${configOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {configOpen && (
                      <div className="space-y-3">
                        <CopyField
                          label="Entity ID"
                          value={sso.spEntityId}
                          hint="Your IdP's Audience URI / Entity ID"
                          sensitive
                        />
                        <CopyField
                          label="ACS URL"
                          value={sso.spACSUrl}
                          hint="Your IdP's Single Sign-On URL / ACS URL"
                          sensitive
                        />
                        <RequiredIdpSettings />
                      </div>
                    )}
                  </div>

                  {noLinkedEmailDomain ? (
                    <div className="border-t pt-3">
                      <p className="text-xs text-muted-foreground">
                        Update SSO (metadata URL and domain) is unavailable
                        until an email domain is linked. Disconnect above, then
                        set up SSO again with a domain that is not already in
                        use.
                      </p>
                    </div>
                  ) : (
                    <div className="border-t pt-3">
                      <button
                        type="button"
                        onClick={handleToggleForm}
                        className="flex w-full items-center justify-between text-left mb-3"
                      >
                        <div>
                          <h4 className="text-sm font-medium">
                            Update SSO configuration
                          </h4>
                          {!isFormOpen && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Paste a new metadata URL to update the SAML
                              connection.
                            </p>
                          )}
                        </div>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isFormOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {isFormOpen && renderConfigForm('space-y-3')}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs font-medium">
                        1
                      </span>
                      <p
                        className={`text-sm font-medium ${sso.ssoId ? 'text-muted-foreground line-through' : ''}`}
                      >
                        Generate SP credentials
                      </p>
                    </div>
                    {!sso.ssoId && (
                      <div className="ml-7 space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Creates your final ACS URL and SSO ID — required
                          before configuring any identity provider.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 border-t pt-4">
                    <div className="flex items-center gap-2">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs font-medium">
                        2
                      </span>
                      <p className="text-sm font-medium">
                        Configure your identity provider
                      </p>
                    </div>
                    <div className="ml-7 space-y-3">
                      <p className="text-xs text-muted-foreground">
                        Copy these values into your IdP when creating the SAML
                        application, then continue to Step 3.
                      </p>
                      <CopyField
                        label="Entity ID"
                        value={sso.spEntityId}
                        hint="Paste into your IdP's Audience URI / Entity ID field"
                        sensitive
                        disableReveal={readOnly}
                      />
                      <CopyField
                        label="ACS URL"
                        value={sso.ssoId ? sso.spACSUrl : ''}
                        hint={
                          sso.ssoId
                            ? "Paste into your IdP's Single Sign-On URL / ACS URL field"
                            : 'Available after completing Step 1'
                        }
                        sensitive
                      />
                      <RequiredIdpSettings />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <button
                      type="button"
                      onClick={() => setFormOpen(o => !(o ?? true))}
                      className="flex w-full items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-2 flex-1 mr-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs font-medium">
                          3
                        </span>
                        <div>
                          <h4 className="text-sm font-medium">
                            Connect your identity provider
                          </h4>
                          {!isFormOpen && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Paste the SAML Metadata URL from your identity
                              provider.
                            </p>
                          )}
                        </div>
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isFormOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isFormOpen && renderConfigForm('ml-7 space-y-3 mt-3')}
                  </div>
                </>
              )}

              <div className="border-t pt-4">
                <button
                  type="button"
                  onClick={() => setOktaOpen(o => !o)}
                  className="flex w-full items-center justify-between text-left"
                >
                  <div className="flex items-center gap-2">
                    <svg
                      viewBox="0 0 64 64"
                      className="h-4 w-4 flex-shrink-0"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-label="Okta"
                    >
                      <circle cx="32" cy="32" r="32" fill="#007DC1" />
                      <circle cx="32" cy="32" r="14" fill="white" />
                    </svg>
                    <h4 className="text-sm font-medium">Using Okta (OIN)?</h4>
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${oktaOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {oktaOpen && (
                  <div className="ml-6 space-y-3 mt-3">
                    <p className="text-xs text-muted-foreground">
                      Find Kirin in the Okta Integration Network App Catalog and
                      use these values when prompted.{' '}
                      <a
                        href="/docs/okta-saml-setup-guide.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
                      >
                        Setup guide
                        <ExternalLink className="h-3 w-3" />
                      </a>
                      {!sso.ssoId && (
                        <span className="text-amber-600 dark:text-amber-400">
                          {' '}
                          — Complete Step 1 first to unlock the SSO ID.
                        </span>
                      )}
                    </p>
                    <CopyField
                      label="Kirin Tenant ID"
                      value={sso.tenantId}
                      hint="Paste into the 'Kirin Tenant ID' field in the OIN app"
                      sensitive
                    />
                    <CopyField
                      label="Kirin SSO ID"
                      value={sso.ssoId}
                      hint="Paste into the 'Kirin SSO ID' field in the OIN app"
                      sensitive
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 px-6 py-4 border-t gap-2 sm:flex-row sm:justify-end">
          {sso?.isConfigured && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive sm:mr-auto"
              onClick={onRequestDisconnect}
            >
              Disconnect
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          {renderFooterPrimary()}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

ConnectSamlSsoModal.displayName = 'ConnectSamlSsoModal';
